import React, { useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckIcon from "@mui/icons-material/Check";
import { CircularProgress, Divider, ListItemIcon } from "@mui/material";
import { observer } from "mobx-react";
import { userStore } from "../../../../stores/sharedStores";
import {
  useActiveWorkspace,
  switchWorkspace,
  WORKSPACES,
} from "../../../../utils/WorkspaceContext";
import { swayyColors, swayyFont, swayyRadii } from "../../theme/tokens";

const SWAYY_LOGO_SRC = "/swayylight.png";

type AppBarProps = {
  handleMenuClick: () => void;
};

const Navbar = observer(({ handleMenuClick }: AppBarProps) => {
  const [profileAnchorEl, setProfileAnchorEl] =
    useState<null | HTMLElement>(null);
  const [workspaceAnchorEl, setWorkspaceAnchorEl] =
    useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const activeWorkspace = useActiveWorkspace();

  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const isWorkspaceMenuOpen = Boolean(workspaceAnchorEl);
  const activeWorkspaceLabel =
    WORKSPACES.find((w) => w.id === activeWorkspace)?.label ?? "";

  const handleLogOut = async () => {
    try {
      setLoading(true);
      await userStore.logOut();
    } catch (_error) {
      console.error("Logout failed", _error);
    } finally {
      setLoading(false);
    }
  };

  const userInitial = userStore.user?.name?.charAt(0)?.toUpperCase() || "U";

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: swayyColors.white,
          borderBottom: `1px solid ${swayyColors.border}`,
          color: swayyColors.deepGreen,
          borderRadius: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            minHeight: "56px !important",
            px: { xs: 1.5, sm: 2.5 },
            gap: 1,
          }}
        >
          <IconButton
            size="medium"
            edge="start"
            aria-label="open drawer"
            onClick={handleMenuClick}
            sx={{
              color: swayyColors.textSecondary,
              borderRadius: 0,
              mr: 0.5,
              "&:hover": { bgcolor: swayyColors.pineLight },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          <Box
            onClick={(e) => setWorkspaceAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              borderRadius: 0,
              px: 0.75,
              py: 0.25,
              "&:hover": { bgcolor: swayyColors.pineLight },
            }}
          >
            <Box
              component="img"
              src={SWAYY_LOGO_SRC}
              alt="Swayy"
              sx={{
                height: 22,
                width: 22,
                objectFit: "contain",
                borderRadius: swayyRadii.sm,
                flexShrink: 0,
                display: "block",
              }}
            />
            <Typography
              sx={{
                fontSize: { sm: 15, md: 16 },
                fontWeight: 600,
                color: swayyColors.deepGreen,
                fontFamily: swayyFont,
                letterSpacing: "-0.01em",
              }}
            >
              {activeWorkspaceLabel}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{ fontSize: 16, color: swayyColors.textSecondary }}
            />
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton
            onClick={(e) => setProfileAnchorEl(e.currentTarget)}
            sx={{
              p: 0.5,
              "&:hover": { bgcolor: swayyColors.pineLight },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: swayyRadii.full,
                bgcolor: swayyColors.pine,
                color: swayyColors.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: swayyFont,
              }}
            >
              {userInitial}
            </Box>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={workspaceAnchorEl}
        open={isWorkspaceMenuOpen}
        onClose={() => setWorkspaceAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            minWidth: 220,
            border: `1px solid ${swayyColors.border}`,
            borderRadius: 0,
          },
        }}
      >
        <Typography
          sx={{
            px: 2,
            pt: 1,
            pb: 0.5,
            fontSize: 11,
            fontWeight: 600,
            color: swayyColors.textSecondary,
            textTransform: "uppercase",
            fontFamily: swayyFont,
            letterSpacing: "0.06em",
          }}
        >
          Switch Workspace
        </Typography>
        {WORKSPACES.map((workspace) => (
          <MenuItem
            key={workspace.id}
            selected={workspace.id === activeWorkspace}
            onClick={() => {
              setWorkspaceAnchorEl(null);
              if (workspace.id !== activeWorkspace) {
                switchWorkspace(workspace.id);
              }
            }}
          >
            {workspace.id === "swayy" && (
              <Box
                component="img"
                src={SWAYY_LOGO_SRC}
                alt=""
                sx={{
                  height: 20,
                  width: 20,
                  objectFit: "contain",
                  borderRadius: swayyRadii.sm,
                  mr: 1,
                  flexShrink: 0,
                }}
              />
            )}
            {workspace.label}
            {workspace.id === activeWorkspace && (
              <CheckIcon
                sx={{ ml: "auto", fontSize: 14, color: swayyColors.pine }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={profileAnchorEl}
        open={isProfileMenuOpen}
        onClose={() => setProfileAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            minWidth: 200,
            border: `1px solid ${swayyColors.border}`,
            borderRadius: 0,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              fontFamily: swayyFont,
              color: swayyColors.deepGreen,
            }}
          >
            {userStore.user?.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: swayyColors.textSecondary,
              mt: 0.25,
              fontFamily: swayyFont,
            }}
          >
            {userStore.user?.email || "Swayy Team"}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5, borderColor: swayyColors.border }} />
        <MenuItem
          onClick={handleLogOut}
          disabled={loading}
          sx={{
            mx: 0.5,
            my: 0.25,
            color: swayyColors.danger,
            fontFamily: swayyFont,
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            {loading ? (
              <CircularProgress size={14} />
            ) : (
              <LogoutIcon fontSize="small" sx={{ color: swayyColors.danger }} />
            )}
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
});

export default Navbar;
